import React, { useMemo, useState, useEffect } from 'react';
import { useQRCode } from '@rc-component/qrcode/es/hooks/useQRCode';
import { excavateModules } from '@rc-component/qrcode/es/utils';

/**
 * Convert an image URL to an inline base64 data URL.
 * This ensures the image is embedded in the SVG, making exports portable.
 */
function useBase64Image(src) {
    const [dataUrl, setDataUrl] = useState(null);
    useEffect(() => {
        if (!src) { setDataUrl(null); return; }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            setDataUrl(canvas.toDataURL('image/png'));
        };
        img.onerror = () => setDataUrl(src); // fallback to original
        img.src = src;
    }, [src]);
    return dataUrl;
}

/**
 * Check if a cell (row, col) is within a 7x7 finder pattern region.
 */
function isFinderPattern(row, col, numCells) {
    if (row < 7 && col < 7) return true;
    if (row < 7 && col >= numCells - 7) return true;
    if (row >= numCells - 7 && col < 7) return true;
    return false;
}

/**
 * Generate an SVG path string from a subset of modules.
 */
function generateFilteredPath(cells, margin, filterFn) {
    const ops = [];
    cells.forEach((row, y) => {
        let start = null;
        row.forEach((cell, x) => {
            const include = cell && filterFn(y, x, cells.length);
            if (!include && start !== null) {
                ops.push(`M${start + margin} ${y + margin}h${x - start}v1H${start + margin}z`);
                start = null;
                return;
            }
            if (x === row.length - 1) {
                if (!include) {
                    if (start !== null) {
                        ops.push(`M${start + margin} ${y + margin}h${x - start}v1H${start + margin}z`);
                    }
                    return;
                }
                if (start === null) {
                    ops.push(`M${x + margin},${y + margin} h1v1H${x + margin}z`);
                } else {
                    ops.push(`M${start + margin},${y + margin} h${x + 1 - start}v1H${start + margin}z`);
                }
                return;
            }
            if (include && start === null) {
                start = x;
            }
        });
    });
    return ops.join('');
}

/**
 * Custom QR code component with colored finder patterns and optional center icon.
 *
 * Props:
 *   - value: string
 *   - size: number (pixel size, default 180)
 *   - errorLevel: 'L' | 'M' | 'Q' | 'H' (default 'H')
 *   - color: string (data module color, default '#000000')
 *   - bgColor: string (background, default '#ffffff')
 *   - finderColor: string (finder pattern color, default '#38b549')
 *   - icon: string (URL/import of center image)
 *   - iconSize: number (pixel size of the icon, default 48)
 *   - style: object
 */
const ColoredQRCode = ({
    value,
    size = 180,
    errorLevel = 'H',
    color = '#000000',
    bgColor = '#ffffff',
    finderColor = '#38b549',
    icon,
    iconSize = 48,
    style
}) => {
    // Convert the icon URL to an inline base64 data URL for portable exports
    const iconDataUrl = useBase64Image(icon);

    const { margin, cells, numCells, calculatedImageSettings } = useQRCode({
        value,
        level: errorLevel,
        minVersion: 1,
        includeMargin: false,
        marginSize: undefined,
        imageSettings: icon ? {
            src: icon,
            height: iconSize,
            width: iconSize,
            excavate: true,
            x: undefined,
            y: undefined,
        } : undefined,
        size,
    });

    const { finderPath, dataPath } = useMemo(() => {
        if (!cells || cells.length === 0) return { finderPath: '', dataPath: '' };

        // If there's an icon, excavate (clear) the center cells
        let cellsToDraw = cells;
        if (calculatedImageSettings && calculatedImageSettings.excavation) {
            cellsToDraw = excavateModules(cells, calculatedImageSettings.excavation);
        }

        const n = cellsToDraw.length;
        const finderPath = generateFilteredPath(cellsToDraw, margin, (r, c) => isFinderPattern(r, c, n));
        const dataPath = generateFilteredPath(cellsToDraw, margin, (r, c) => !isFinderPattern(r, c, n));
        return { finderPath, dataPath };
    }, [cells, margin, calculatedImageSettings]);

    return (
        <svg
            height={size}
            width={size}
            viewBox={`0 0 ${numCells} ${numCells}`}
            role="img"
            style={style}
        >
            {/* Background */}
            <path fill={bgColor} d={`M0,0 h${numCells}v${numCells}H0z`} shapeRendering="crispEdges" />
            {/* Data modules */}
            <path fill={color} d={dataPath} shapeRendering="crispEdges" />
            {/* Finder patterns */}
            <path fill={finderColor} d={finderPath} shapeRendering="crispEdges" />
            {/* Center icon (embedded as base64 for portable export) */}
            {iconDataUrl && calculatedImageSettings && (
                <image
                    href={iconDataUrl}
                    x={calculatedImageSettings.x + margin}
                    y={calculatedImageSettings.y + margin}
                    width={calculatedImageSettings.w}
                    height={calculatedImageSettings.h}
                    preserveAspectRatio="xMidYMid meet"
                />
            )}
        </svg>
    );
};

export default ColoredQRCode;
